/**
 * Kliendiotsing: the prospect funnel's addresses, in one table.
 *
 * Five sidebar rows used to be five pages - the audits desk, the scanner, its
 * leads, the development brain and the follow-up queue - that are the steps of
 * one job, drawn as five unrelated destinations in a marketing group already
 * thirteen rows long. They are now the tabs of one page, `/prospects`, in
 * the order the work happens: the sites that came to us, the sites we scanned,
 * the ones worth a call, what the brain found, the defects that give a reason
 * to ring today, and the promises to ring back (docs/kliendiotsing-leht-plaan.md).
 *
 * This file is the pure half of that page. It knows which tabs exist, which one
 * a URL is on, where each retired address lives now and how a link into a tab
 * is spelt. It imports nothing from React on purpose: `nav.ts`, the link
 * builders in `lib/` and the tests all read it, and none of them may need a DOM
 * to do so.
 *
 * The bodies of the tabs did NOT move. Each still lives in its own folder
 * (`site-scanner/`, `scan-leads/`, `site-audits/`, `brain/`, `brain-due/`),
 * because about thirty files import `site-scanner/types` alone and a folder
 * move would have been a forty-file diff that bought nothing.
 */

/** The page's own address. The index redirects to `HOME_STEP`. */
export const PROSPECTS = "/prospects";

/** A tab's identity and its URL segment under `/prospects`, one and the same. */
export type StepKey = "inbound" | "scans" | "leads" | "brain" | "incidents" | "follow-up";

export interface ProspectStep {
  key: StepKey;
  labelKey: string;
  /**
   * The one sentence under the page title while this tab is open: what the tab
   * is for. The page has one head (`ProspectingSection`), and the sentence in
   * it follows the tab, because "Kliendiotsing" alone says which page this is
   * and nothing about the list below it. Each tab used to carry this sentence
   * in a head of its own, over a title that repeated the tab's name.
   */
  introKey: string;
  /**
   * The sentence instead of `introKey` while one record below the tab is open
   * (`/prospects/brain/:domain`). The list's sentence tells the operator to
   * open a company, which is the wrong thing to read with one already open.
   * Only a `nested` tab has anything below it to open.
   */
  detailIntroKey?: string;
  /** Owns sub-routes and therefore needs the `/*` suffix. Only the brain does:
   *  `/prospects/brain/:domain` is one company on its own address. */
  nested?: boolean;
}

/**
 * The tabs, in funnel order.
 *
 * Inbound comes first although nobody on our side started it: an audit left on
 * our public page is the one prospect that has already raised a hand, and the
 * funnel read from that end is where the week's warmest names are. Incidents
 * sit after the brain because the watch list is built from the brain's fit
 * verdicts, and follow-ups come last because a promise to ring back is born at
 * the end of a call.
 */
export const STEPS: readonly ProspectStep[] = [
  { key: "inbound", labelKey: "prospecting.tabs.inbound", introKey: "prospecting.intro.inbound" },
  { key: "scans", labelKey: "prospecting.tabs.scans", introKey: "prospecting.intro.scans" },
  { key: "leads", labelKey: "prospecting.tabs.leads", introKey: "prospecting.intro.leads" },
  {
    key: "brain",
    labelKey: "prospecting.tabs.brain",
    introKey: "prospecting.intro.brain",
    detailIntroKey: "prospecting.intro.brainCompany",
    nested: true,
  },
  { key: "incidents", labelKey: "prospecting.tabs.incidents", introKey: "prospecting.intro.incidents" },
  { key: "follow-up", labelKey: "prospecting.tabs.followUp", introKey: "prospecting.intro.followUp" },
];

/**
 * Where `/prospects` itself lands: the call queue. That is where the week's
 * work starts, and it is where the bell's "new leads" row already pointed.
 */
export const HOME_STEP: StepKey = "leads";

/** The address of one tab. */
export function stepPath(key: StepKey): string {
  return `${PROSPECTS}/${key}`;
}

/**
 * Which tab a pathname is on, or null when it is on none of them.
 *
 * Strict about depth for the same reason the router is: a step without
 * sub-routes does not own `/prospects/scans/anything`, and the router sends that
 * address back to the home tab. Answering "scans" for it would describe a page
 * the operator never sees.
 */
export function stepFromPath(pathname: string): StepKey | null {
  if (!pathname.startsWith(`${PROSPECTS}/`)) return null;
  const [segment = "", ...rest] = pathname.slice(PROSPECTS.length + 1).split("/");
  const step = STEPS.find((s) => s.key === segment);
  if (!step) return null;
  if (!step.nested && rest.some((part) => part !== "")) return null;
  return step.key;
}

/**
 * The sentence under the page title for a pathname, or null when the pathname
 * is on no tab (the one frame before an unknown address is sent home).
 *
 * The tab's own sentence, except below a nested tab: one company's brain is
 * not the list of companies, and the head says so.
 */
export function introKeyFor(pathname: string): string | null {
  const key = stepFromPath(pathname);
  const step = STEPS.find((s) => s.key === key);
  if (!step) return null;
  const below = pathname.slice(stepPath(step.key).length).split("/").some((part) => part !== "");
  return below && step.detailIntroKey ? step.detailIntroKey : step.introKey;
}

/**
 * The retired addresses and the tab each one now opens.
 *
 * `keepRest` says whether the path below the old address carries meaning. Only
 * the brain's does - `/brain/<domain>` is one company - and it is appended as it
 * arrived, still percent-encoded, so a domain with anything odd in it lands on
 * the same record rather than on a decoded and re-split copy of it.
 */
const LEGACY: Record<string, { step: StepKey; keepRest: boolean }> = {
  "/site-scanner": { step: "scans", keepRest: false },
  "/scan-leads": { step: "leads", keepRest: false },
  "/site-audits": { step: "inbound", keepRest: false },
  "/brain": { step: "brain", keepRest: true },
  "/brain-due": { step: "follow-up", keepRest: false },
};

/** The retired addresses, for the router table and its test. */
export const LEGACY_PATHS: readonly string[] = Object.keys(LEGACY);

/**
 * Where a retired address now lives, query string included, or null when the
 * pathname is not one of them.
 *
 * The query string is the whole point of this function. React Router's
 * `<Navigate to>` drops `location.search` unless it is copied by hand, and the
 * old addresses were mostly reached WITH one: every scanner lead's source badge
 * links `/site-scanner?url=<domain>`, and a call's globe button links
 * `/scan-leads?q=<domain>`. A redirect that lost it would land every bookmark
 * and every pasted link on an empty tab.
 *
 * The match is on whole segments, so `/brain-due` is never read as a company
 * called "-due" under `/brain`.
 */
export function legacyTarget(pathname: string, search: string): string | null {
  for (const [prefix, { step, keepRest }] of Object.entries(LEGACY)) {
    if (pathname !== prefix && !pathname.startsWith(`${prefix}/`)) continue;
    const rest = keepRest ? pathname.slice(prefix.length).replace(/\/+$/, "") : "";
    return `${stepPath(step)}${rest}${querySuffix(search)}`;
  }
  return null;
}

/** `search` as a suffix: with its `?`, or nothing at all when it is empty. */
function querySuffix(search: string): string {
  const bare = search.startsWith("?") ? search.slice(1) : search;
  return bare ? `?${bare}` : "";
}

/**
 * One scan report, opened on the Skannid tab. `?url=` is the parameter the
 * scanner body reads on arrival to open that report straight away.
 */
export function scanReportPath(domain: string): string {
  return `${stepPath("scans")}?url=${encodeURIComponent(domain)}`;
}

/** The call queue, searched for one domain. */
export function leadSearchPath(domain: string): string {
  return `${stepPath("leads")}?q=${encodeURIComponent(domain)}`;
}

/** One company's brain, on its own address under the Arendusaju tab. */
export function brainCompanyPath(domain: string): string {
  return `${stepPath("brain")}/${encodeURIComponent(domain)}`;
}
